import React, { useEffect, useState } from "react";
import { useRole } from "../context/RoleContext";
import { api } from "../lib/api";
import { getAdminSettings } from "../lib/adminPortal";
import { Toaster, toast } from "sonner";
import { AlertTriangle, Mail } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminSettings() {
  const { user } = useRole();
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user?.id) return;
    setSummary(await getAdminSettings(user.id));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const onReset = async () => {
    if (confirmText.trim() !== "RESET") return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/reset-demo-data?user_id=${encodeURIComponent(user.id)}`,
      );
      toast.success("Platform reset to seed baseline.", {
        description: `${data.restored_seed_records} seed records restored, ${data.total_deleted} session records deleted.`,
      });
      setOpen(false);
      setConfirmText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="admin-settings-page">
      <Toaster position="top-right" richColors closeButton />

      <div className="flex items-center gap-3">
        <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
        <span className="wn-label" style={{ color: "#8F3C23" }}>Platform Admin · Settings</span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(26px, 3.6vw, 32px)",
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Platform Settings.
      </h1>
      <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 6 }}>
        Operations and demo-management controls.
      </p>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 16 }} />

      <div className="mt-7 max-w-2xl">
        <div
          className="wn-card"
          style={{ padding: 22 }}
          data-testid="admin-demo-card"
        >
          <h2
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 20,
              color: "#1B1F4B",
            }}
          >
            Demo Data Management.
          </h2>
          <p style={{ color: "#272636", fontSize: 13.5, lineHeight: 1.6, marginTop: 8 }}>
            This platform runs on a curated seed dataset for the demo, with all user actions
            persisted as a separate demo layer. Reset Demo Data returns the platform to its
            seed baseline.
          </p>

          {summary ? (
            <dl
              className="mt-5 grid grid-cols-3 gap-4 tabular-nums"
              data-testid="admin-demo-counts"
            >
              <Counter label="Demo records" v={summary.demo_records} />
              <Counter label="Demo uploaded files" v={summary.demo_uploaded_files} />
              <Counter label="Modified seed records" v={summary.modified_seed_records} />
            </dl>
          ) : (
            <div className="mt-5" style={{ color: "#6C6B85", fontStyle: "italic" }}>
              Loading demo-layer counts…
            </div>
          )}

          <div
            className="mt-5"
            style={{ borderTop: "1px solid #D9D8E4", paddingTop: 16 }}
          >
            {!open ? (
              <button
                className="wn-btn wn-btn-outline"
                style={{ color: "#A23B2E", borderColor: "#1B1F4B" }}
                onClick={() => setOpen(true)}
                data-testid="admin-reset-open"
              >
                <AlertTriangle size={14} /> Reset Demo Data
              </button>
            ) : (
              <div data-testid="admin-reset-form">
                <h3
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 17,
                    color: "#1B1F4B",
                  }}
                >
                  Reset all demo data?
                </h3>
                <p style={{ color: "#272636", fontSize: 13, lineHeight: 1.6, marginTop: 6 }}>
                  This will delete every record created during use and restore every modified
                  seed record to its original state. Uploaded files attached to demo records
                  will be removed. The seed dataset itself is preserved.
                </p>

                <label className="wn-label mt-4 block">
                  Type <span style={{ color: "#A23B2E", fontWeight: 800 }}>RESET</span> to confirm
                </label>
                <input
                  className="wn-input tabular-nums"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESET"
                  autoFocus
                  data-testid="admin-reset-confirm-input"
                />

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    className="wn-btn wn-btn-tertiary"
                    onClick={() => { setOpen(false); setConfirmText(""); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="wn-btn"
                    style={{
                      background: confirmText.trim() === "RESET" ? "#A23B2E" : "#D9D8E4",
                      color: "#FBFAFD",
                      cursor: confirmText.trim() === "RESET" ? "pointer" : "not-allowed",
                    }}
                    onClick={onReset}
                    disabled={confirmText.trim() !== "RESET" || busy}
                    data-testid="admin-reset-confirm-btn"
                  >
                    {busy ? "Resetting…" : "Confirm Reset"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="wn-card mt-5"
          style={{ padding: 22 }}
          data-testid="admin-email-log-card"
        >
          <h2
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 20,
              color: "#1B1F4B",
            }}
          >
            Simulated email log.
          </h2>
          <p style={{ color: "#272636", fontSize: 13.5, lineHeight: 1.6, marginTop: 8 }}>
            Every notification email this platform <em style={{ fontFamily: '"Newsreader", Georgia, serif' }}>would have sent</em> is recorded for demo transparency. No real email is dispatched in this PoC.
          </p>
          <div className="mt-4">
            <Link
              to="/admin/email-log"
              className="wn-btn wn-btn-outline"
              style={{ color: "#1B1F4B" }}
              data-testid="admin-email-log-link"
            >
              <Mail size={14} /> Open simulated email log
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Counter({ label, v }) {
  return (
    <div>
      <div
        className="tabular-nums"
        style={{ fontWeight: 800, fontSize: 22, color: "#1B1F4B" }}
      >
        {v}
      </div>
      <div className="wn-label">{label}</div>
    </div>
  );
}
